import {
  AlertTriangle,
  Archive,
  FileQuestion,
  LoaderCircle,
  LockKeyhole,
  WifiOff,
} from 'lucide-react'
import { ReactNode } from 'react'
import { Link, useRouteError } from 'react-router'
import { ApiError } from '../api'
import { useDocumentTitle } from '../hooks/useDocumentTitle'

export function LoadingState({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="state-panel" role="status">
      <LoaderCircle className="spin" />
      <strong>{label}</strong>
    </div>
  )
}

export function EmptyState({
  title,
  detail,
  action,
}: {
  title: string
  detail?: string
  action?: ReactNode
}) {
  return (
    <div className="state-panel empty">
      <FileQuestion />
      <strong>{title}</strong>
      {detail && <p>{detail}</p>}
      {action}
    </div>
  )
}

export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  const apiError = error instanceof ApiError ? error : null
  const offline = apiError?.code === 'offline'
  const Icon = offline ? WifiOff : AlertTriangle
  return (
    <div className="state-panel error" role="alert">
      <Icon />
      <strong>{apiError?.message || 'Something went wrong'}</strong>
      {apiError?.requestId && <small>Request {apiError.requestId}</small>}
      {retry && (
        <button className="secondary-button" onClick={retry}>
          Try again
        </button>
      )}
    </div>
  )
}

export function RouteErrorBoundary() {
  const error = useRouteError()
  useDocumentTitle('Error')
  return (
    <main className="resource-screen">
      <AlertTriangle />
      <h1>That view could not be opened.</h1>
      <p>{error instanceof Error ? error.message : 'An unexpected route error occurred.'}</p>
      <Link className="primary-button" to="/">
        Repositories
      </Link>
    </main>
  )
}

export function ResourceScreen({ kind }: { kind: '403' | '404' | 'archived' | 'deleted' }) {
  const content = {
    '403': {
      icon: <LockKeyhole />,
      title: 'Permission denied',
      detail: 'Your account does not have access to this resource.',
    },
    '404': {
      icon: <FileQuestion />,
      title: 'Resource not found',
      detail: 'It may not exist, or it may belong to another organization.',
    },
    archived: {
      icon: <Archive />,
      title: 'Resource archived',
      detail: 'Restore it before making changes.',
    },
    deleted: {
      icon: <FileQuestion />,
      title: 'Resource deleted',
      detail: 'This resource is no longer available.',
    },
  }[kind]
  useDocumentTitle(content.title)
  return (
    <main className="resource-screen">
      {content.icon}
      <h1>{content.title}</h1>
      <p>{content.detail}</p>
      <Link className="primary-button" to="/">
        Repositories
      </Link>
    </main>
  )
}
